// store/analogGeneratorSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * AsyncThunk для поиска аналогов бренда через сокет.
 * Параметры: brandName, socket.
 * Ожидается, что в ответе от сервера в случае успеха поле analogs содержит массив строк
 * либо строку в формате "[Analog1, Analog2, ...]".
 */
export const generateAnalogs = createAsyncThunk(
  'analogGenerator/generate',
  async ({ brandName, socket }, { rejectWithValue }) => {
    const MAX_RETRIES = 3;
    let lastError;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await new Promise((resolve) => {
          socket.emit('find_similar_analogs', { 
            analogName: brandName,
            quantity: 10,
            suggestionId: 'main_analogs'
          }, (response) => {
            resolve(response);
          });
        });
        if (response.status === 'ok') {
          let analogs = response.similarAnalogs;
          // Если пришла строка, пытаемся её распарсить
          if (typeof analogs === 'string') {
            analogs = analogs.trim();
            if (analogs.startsWith('[') && analogs.endsWith(']')) {
              analogs = analogs.slice(1, -1); // удаляем квадратные скобки
              analogs = analogs.split(',').map(item => item.trim()); // разбиваем по запятой и убираем пробелы
              analogs = analogs.filter(item => item.length > 0); // исключаем пустые элементы
            } else {
              analogs = [analogs]; // если это просто строка, делаем её массивом
            }
          }
          // Проверяем, что получили массив строк
          if (Array.isArray(analogs) && analogs.every(item => typeof item === 'string')) {
            return { analogs };
          } else {
            lastError = 'Неверный формат ответа: similarAnalogs не является массивом строк';
          }
        } else {
          lastError = response.message || 'Ошибка при поиске аналогов';
        }
      } catch (error) {
        lastError = error.message || 'Ошибка поиска аналогов';
      }
    }
    return rejectWithValue(lastError || 'Ошибка поиска аналогов');
  }
);

const analogGeneratorSlice = createSlice({
  name: 'analogGenerator',
  initialState: {
    analogs: [],
    loading: false,
    error: null,
    // Поле для хранения результатов поиска похожих аналогов
    similar: {} // ключ – id карточки, значение: { loading, error, data }
  },
  reducers: {
    clearAnalogs: (state) => {
      state.analogs = [];
      state.loading = false;
      state.error = null;
    },
    // Редюсеры для поиска похожих аналогов
    similarAnalogSearchStart: (state, action) => {
      const suggestionId = action.payload;
      state.similar[suggestionId] = { loading: true, error: null, data: null };
    },
    similarAnalogSearchSuccess: (state, action) => {
      const { suggestionId, data } = action.payload;
      state.similar[suggestionId] = { loading: false, error: null, data };
    },
    similarAnalogSearchError: (state, action) => {
      const { suggestionId, error } = action.payload;
      state.similar[suggestionId] = { loading: false, error, data: [] };
    },
    clearSimilarAnalogResults: (state, action) => {
      const suggestionId = action.payload;
      delete state.similar[suggestionId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateAnalogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateAnalogs.fulfilled, (state, action) => {
        const { analogs } = action.payload;
        state.analogs = analogs;
        state.loading = false;
      })
      .addCase(generateAnalogs.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
      });
  },
});

export const { 
  clearAnalogs, 
  similarAnalogSearchStart, 
  similarAnalogSearchSuccess, 
  similarAnalogSearchError, 
  clearSimilarAnalogResults,
} = analogGeneratorSlice.actions;
export default analogGeneratorSlice.reducer; 